import { OrderItemCard } from "./ToolsMobileCards";
import { useToolsMobile } from "./ToolsLayout";
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowBack } from "@mui/icons-material";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { MarketStatus, RefreshTools, ToolsState } from "./ToolsLayout";
import { invalidateTools, money, number, orderTotal, toolsDate } from "./toolsData";
import PaymentReviewDialog from "./PaymentReviewDialog";
import ReceiptPreview from "./ReceiptPreview";

const transitions = {
  accept: {
    title: "Approve order",
    status: "accepted",
    from: "pending",
    message: "Confirm the shipping charge before approving this order.",
  },
  reject: {
    title: "Reject order",
    status: "rejected",
    from: "pending",
    message: "Confirm that this order should be rejected.",
  },
  ship: {
    title: "Mark as shipped",
    status: "shipped",
    from: "accepted",
    message: "Confirm that the order has been dispatched to the customer.",
  },
  deliver: {
    title: "Mark as delivered",
    status: "delivered",
    from: "shipped",
    message: "Confirm that the customer has received this order.",
  },
};
const SummaryRow = ({ label, value, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.7 }}>
    <Typography
      variant="body2"
      color={bold ? "text.primary" : "text.secondary"}
      fontWeight={bold ? 800 : 400}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 800 : 500}>
      {value}
    </Typography>
  </Box>
);

export default function ToolsOrderDetails() {
  const mobile = useToolsMobile();
  const { id } = useParams();
  const query = useApiEp(`admin/get-tools-order/${encodeURIComponent(id)}`);
  const client = useQueryClient();
  const [action, setAction] = useState(""),
    [shipping, setShipping] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [paymentOpen, setPaymentOpen] = useState(false);
  const order = query.data;
  const config = transitions[action];
  const openAction = (next) => {
    setAction(next);
    setShipping(String(order?.orderSummary?.shipping ?? 0));
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving || !config || !order) return;
    if (
      action === "accept" &&
      (shipping === "" || !Number.isFinite(Number(shipping)) || Number(shipping) < 0)
    ) {
      setError("Enter a shipping charge of zero or more.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fresh = await query.refetch();
      if (fresh.isError || !fresh.data)
        throw new Error("Unable to verify the order. Please retry.");
      if (fresh.data.orderStatus !== config.from)
        throw new Error(
          "This order status has changed. Close the dialog and review the updated order.",
        );
      if (orderTotal(fresh.data) !== orderTotal(order))
        throw new Error(
          "The order total changed while you were reviewing. Close the dialog and check the updated charges.",
        );
      const url = `${API_ENDPOINT}admin/${["accept", "reject"].includes(action) ? "accept-reject-order" : "update-order-status"}/${encodeURIComponent(id)}`;
      const body = {
        status: config.status,
        ...(action === "accept" && { shipping: Number(shipping) }),
      };
      await axios({
        url,
        method: ["accept", "reject"].includes(action) ? "post" : "patch",
        data: body,
        ...getHeader(),
      });
      await invalidateTools(client);
      setNotice(`${config.title}: order ${id} updated successfully.`);
      setAction("");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Unable to update order. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };
  const total = orderTotal(order);
  const steps = ["pending", "accepted", "shipped", "delivered"];
  const activeStep = steps.indexOf(order?.orderStatus);
  const reviewable = order && !["paid", "rejected"].includes(order.paymentStatus);
  return (
    <ToolsLayout
      title={order ? `Order ${order.orderId || id}` : "Order details"}
      description={
        order
          ? `Placed ${toolsDate(order.createdOn)} · ${order.orderType || "Purchase"} order`
          : "Review items, payment evidence, and delivery details."
      }
      actions={
        <>
          <Button component={Link} to="/home/tools-orders" startIcon={<ArrowBack />}>
            Orders
          </Button>
          <RefreshTools onClick={query.refetch} loading={query.isFetching} />
        </>
      }
    >
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      <ToolsState
        loading={query.isLoading}
        error={query.isError}
        retry={query.refetch}
        empty={!order}
        emptyTitle="Order not found"
        emptyMessage="The order may have been removed or the reference is invalid."
      >
        {order && (
          <>
            <section className="tools-panel" style={{ marginBottom: 24 }}>
              <div className="tools-panel-heading">
                <div className="tools-actions">
                  <MarketStatus status={order.orderStatus} />
                  <MarketStatus status={order.paymentStatus} payment />
                </div>
                <div className="tools-actions">
                  {order.orderStatus === "pending" && (
                    <>
                      <Button variant="contained" onClick={() => openAction("accept")}>
                        Approve order
                      </Button>
                      <Button variant="outlined" color="error" onClick={() => openAction("reject")}>
                        Reject order
                      </Button>
                    </>
                  )}
                  {order.orderStatus === "accepted" && (
                    <Button variant="contained" onClick={() => openAction("ship")}>
                      Mark as shipped
                    </Button>
                  )}
                  {order.orderStatus === "shipped" && (
                    <Button variant="contained" onClick={() => openAction("deliver")}>
                      Mark as delivered
                    </Button>
                  )}
                  {reviewable && (
                    <Button variant="outlined" onClick={() => setPaymentOpen(true)}>
                      {order.receiptUrl ? "Review payment receipt" : "Record payment"}
                    </Button>
                  )}
                </div>
              </div>
              {activeStep >= 0 && (
                <Stepper activeStep={activeStep} alternativeLabel sx={{ px: 2, pb: 3 }}>
                  {["Received", "Approved", "Shipped", "Delivered"].map((label, index) => (
                    <Step
                      key={label}
                      completed={index < activeStep || order.orderStatus === "delivered"}
                    >
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </section>
            <div className="tools-two-column">
              <Stack spacing={3} sx={{ minWidth: 0 }}>
                <section className="tools-panel">
                  <div className="tools-panel-heading">
                    <div>
                      <Typography component="h2" variant="h6">
                        Order items
                      </Typography>
                      <p>{order.items?.length || 0} product lines</p>
                    </div>
                  </div>
                  {mobile ? (
                    <div>
                      {(order.items || []).map((item, index) => (
                        <OrderItemCard key={item._id || index} item={item} />
                      ))}
                    </div>
                  ) : (
                    <TableContainer>
                      <Table aria-label="Order items">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Units</TableCell>
                            <TableCell align="right">Unit price</TableCell>
                            <TableCell align="right">GST</TableCell>
                            <TableCell align="right">Line total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(order.items || []).map((item, index) => (
                            <TableRow key={item._id || index}>
                              <TableCell>
                                <div className="tools-product">
                                  {item.images?.[0] && <img src={item.images[0]} alt="" />}
                                  <Typography variant="body2" fontWeight={800}>
                                    {item.name || "Product unavailable"}
                                  </Typography>
                                </div>
                              </TableCell>
                              <TableCell align="right">{item.quantity ?? "—"}</TableCell>
                              <TableCell align="right">{money(item.price)}</TableCell>
                              <TableCell align="right">
                                {item.gst === null || item.gst === undefined ? "—" : `${item.gst}%`}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800 }}>
                                {money(item.itemTotal)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!order.items?.length && (
                    <Alert severity="info" sx={{ m: 2 }}>
                      Item details are unavailable for this order.
                    </Alert>
                  )}
                </section>
                <section className="tools-panel">
                  <div className="tools-panel-heading">
                    <div>
                      <Typography component="h2" variant="h6">
                        Receipt & payment record
                      </Typography>
                      <p>Review the uploaded evidence alongside the recorded transaction.</p>
                    </div>
                    {reviewable && (
                      <Button variant="outlined" size="small" onClick={() => setPaymentOpen(true)}>
                        Review payment
                      </Button>
                    )}
                  </div>
                  <Box sx={{ px: 3, pb: 3 }}>
                    <ReceiptPreview key={order.receiptUrl} url={order.receiptUrl} />
                    <Divider sx={{ my: 2 }} />
                    <SummaryRow label="Payment method" value={order.paymentMethod || "—"} />
                    <SummaryRow
                      label="Transaction ID"
                      value={order.transactionId || "Not recorded"}
                    />
                    <SummaryRow label="Payment date" value={toolsDate(order.paymentDate)} />
                  </Box>
                </section>
              </Stack>
              <Stack spacing={3}>
                <section className="tools-panel">
                  <div className="tools-panel-heading">
                    <Typography component="h2" variant="h6">
                      Payment breakdown
                    </Typography>
                  </div>
                  <Box sx={{ px: 3, pb: 3 }}>
                    <SummaryRow label="Subtotal" value={money(order.orderSummary?.subtotal)} />
                    <SummaryRow label="GST" value={money(order.orderSummary?.gst)} />
                    <SummaryRow label="Shipping" value={money(order.orderSummary?.shipping ?? 0)} />
                    <Divider sx={{ my: 1 }} />
                    <SummaryRow label="Total payable" value={money(total)} bold />
                    <SummaryRow
                      label="Payment recorded"
                      value={order.paymentStatus === "paid" ? money(total) : "Not marked paid"}
                    />
                    {order.paymentStatus !== "paid" && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Payment has not been approved for this order.
                      </Alert>
                    )}
                    {(order.bill_link || order.billLink) && (
                      <Button
                        sx={{ mt: 2 }}
                        href={order.bill_link || order.billLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open order bill
                      </Button>
                    )}
                  </Box>
                </section>
                <section className="tools-panel">
                  <div className="tools-panel-heading">
                    <Typography component="h2" variant="h6">
                      Customer & delivery
                    </Typography>
                  </div>
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography fontWeight={800}>
                      {order.shippingAddress?.fullName || "Customer unavailable"}
                    </Typography>
                    <Typography variant="body2" sx={{ my: 1 }}>
                      {order.shippingAddress?.phone || "Phone not provided"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, overflowWrap: "anywhere" }}
                    >
                      {order.shippingAddress?.email || "Email not provided"}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {[
                        order.shippingAddress?.address,
                        [order.shippingAddress?.city, order.shippingAddress?.state]
                          .filter(Boolean)
                          .join(", "),
                        order.shippingAddress?.zipCode,
                        order.shippingAddress?.country,
                      ]
                        .filter(Boolean)
                        .join("\n") || "Shipping address unavailable"}
                    </Typography>
                  </Box>
                </section>
              </Stack>
            </div>
          </>
        )}
      </ToolsState>
      <Dialog
        open={Boolean(action)}
        onClose={saving ? undefined : () => setAction("")}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={submit}>
          <DialogTitle>
            {config?.title} · {id}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {config?.message}
            </Typography>
            {action === "accept" && (
              <>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  type="number"
                  label="Shipping charge (₹)"
                  value={shipping}
                  onChange={(event) => {
                    setShipping(event.target.value);
                    setError("");
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Enter 0 for free shipping."
                  disabled={saving}
                />
                <Box sx={{ my: 2 }}>
                  <SummaryRow
                    label="Subtotal + GST"
                    value={money(
                      number(order?.orderSummary?.subtotal) + number(order?.orderSummary?.gst),
                    )}
                  />
                  <SummaryRow label="Shipping" value={shipping === "" ? "—" : money(shipping)} />
                  <SummaryRow
                    label="Total after approval"
                    value={
                      shipping === ""
                        ? "—"
                        : money(
                            number(order?.orderSummary?.subtotal) +
                              number(order?.orderSummary?.gst) +
                              number(shipping),
                          )
                    }
                    bold
                  />
                </Box>
              </>
            )}
            {action === "ship" && order?.paymentStatus !== "paid" && (
              <Alert severity="warning">
                This order's payment has not been approved. Check the payment arrangement before
                dispatching.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setAction("")}>
              Cancel
            </Button>
            <LoadingButton
              loading={saving}
              variant="contained"
              color={action === "reject" ? "error" : "primary"}
              type="submit"
            >
              {config?.title}
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>
      {paymentOpen && (
        <PaymentReviewDialog
          orderId={id}
          onClose={() => setPaymentOpen(false)}
          onSaved={setNotice}
        />
      )}
    </ToolsLayout>
  );
}
